'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { LoginForm } from '@/components/auth/login-form'
import { Menu } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet'

const themes = [
  {
    name: 'green',
    bgGradient: 'from-[var(--brand-green)] via-[#1B8A46] to-[#0A4D22]',
    blob1: 'bg-[var(--brand-yellow)]',
    blob2: 'bg-[var(--brand-orange)]',
    textPos: 'from-[var(--brand-yellow)] to-white',
    textAdmin: 'from-[var(--brand-orange)] to-white',
    formBorderHov: 'hover:shadow-[var(--brand-green)]/10 hover:border-[var(--brand-green)]/30',
    formTopLine: 'via-[var(--brand-green)]',
    mobileAccent1: 'bg-[var(--brand-green)]',
    mobileAccent2: 'bg-[var(--brand-yellow)]',
  },
  {
    name: 'orange',
    bgGradient: 'from-[var(--brand-orange)] via-[#D95A11] to-[#8C3300]',
    blob1: 'bg-[var(--brand-yellow)]',
    blob2: 'bg-[var(--brand-green)]', // Changed from rose to brand-green
    textPos: 'from-[var(--brand-yellow)] to-white',
    textAdmin: 'from-[var(--brand-orange)] to-white', // Changed from rose to brand-orange
    formBorderHov: 'hover:shadow-[var(--brand-orange)]/10 hover:border-[var(--brand-orange)]/30',
    formTopLine: 'via-[var(--brand-orange)]',
    mobileAccent1: 'bg-[var(--brand-orange)]',
    mobileAccent2: 'bg-[var(--brand-yellow)]',
  }
]

export default function LoginPage() {
  const [loginType, setLoginType] = useState<'pos' | 'admin'>('pos')
  const [currentThemeIndex, setCurrentThemeIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentThemeIndex((prev) => (prev + 1) % themes.length)
    }, 7000) // Transición relajada cada 7 segundos
    return () => clearInterval(interval)
  }, [])

  const theme = themes[currentThemeIndex]

  return (
    <div className="min-h-screen w-full flex bg-background relative selection:bg-white/20">
      {/* Left Pane - Visuals (Desktop) */}
      <div className="hidden lg:flex w-1/2 relative flex-col justify-between overflow-hidden p-12 lg:p-20 bg-zinc-950">

        {/* Dynamic Backgrounds Wrapper - Crossfading Themes */}
        <div className="absolute inset-0 z-0">
          {themes.map((t, i) => (
            <div
              key={t.name}
              className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${i === currentThemeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
            >
              {/* Main Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${t.bgGradient}`} />

              {/* Dynamic Glowing Blobs */}
              <div className={`absolute -top-[20%] -right-[10%] w-[80rem] h-[80rem] rounded-full ${t.blob1}/40 blur-[130px] mix-blend-color-dodge opacity-80 animate-[pulse_8s_ease-in-out_infinite]`} />
              <div className={`absolute bottom-[-10%] -left-[20%] w-[60rem] h-[60rem] rounded-full ${t.blob2}/40 blur-[130px] mix-blend-color-dodge opacity-80 animate-[pulse_12s_ease-in-out_infinite_reverse]`} />
            </div>
          ))}
        </div>

        {/* Cinematic Noise Overlay (Glassy Premium Texture) */}
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay z-20 pointer-events-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }} />

        {/* Content Overlays */}
        <div className="relative z-30 flex flex-col justify-between h-full">
          {/* Main Logo: Clicking cycles the theme instantly to see the next one */}
          <div className="pt-8">
            <button
              onClick={() => setCurrentThemeIndex((prev) => (prev + 1) % themes.length)}
              className="inline-block transition-transform duration-700 hover:scale-[1.05] relative group focus:outline-none"
              title="Tip: Haz clic para cambiar el tema de color"
            >
              <div className="absolute inset-0 bg-white/20 blur-[60px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <Image
                src="/Logo247Stop.png"
                alt="24/7 STOP"
                width={480}
                height={480}
                className="object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.5)] relative z-10 group-hover:drop-shadow-[0_0_40px_rgba(255,255,255,0.4)] transition-all duration-700"
                priority
              />
            </button>
          </div>

          {/* Typography overlay */}
          <div className="space-y-6 pb-8">
            <h1 className="text-5xl xl:text-[4.5rem] font-extrabold text-white tracking-tight leading-[1.05] drop-shadow-xl">
              {loginType === 'pos' ? 'Punto de Venta' : 'Administración'} <br />
              {/* the gradient text changes smoothly when the theme variable changes */}
              <span className={`text-transparent bg-clip-text bg-gradient-to-r ${loginType === 'pos' ? theme.textPos : theme.textAdmin} drop-shadow-sm transition-all duration-1000`}>
                Cloud-Based
              </span>
            </h1>
            <p className="text-white/95 max-w-lg text-lg sm:text-xl font-medium leading-relaxed border-l-4 border-white/50 pl-5 drop-shadow-md">
              Gestión total 24/7. Potenciado por <span className="text-white font-bold bg-black/20 px-2 py-0.5 rounded-md">NGI TECH</span> para llevar el control de inventario y caja al siguiente nivel. Experiencia premium y vibrante.
            </p>
          </div>
        </div>
      </div>

      {/* Right Pane - Form Setup */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 sm:p-12 relative overflow-hidden bg-background">

        {/* Mobile Background Accents (Hidden on Desktop) */}
        <div className={`absolute -top-1/4 -right-1/4 w-[400px] h-[400px] rounded-full ${theme.mobileAccent1}/10 blur-[100px] lg:hidden transition-colors duration-1000`} />
        <div className={`absolute -bottom-1/4 -left-1/4 w-[400px] h-[400px] rounded-full ${theme.mobileAccent2}/10 blur-[100px] lg:hidden transition-colors duration-1000`} />

        <div className="w-full max-w-md space-y-8 relative z-10">

          <div className="flex flex-col items-center lg:items-start space-y-6 w-full">
            {/* Mobile Header: Burger Menu & Logo */}
            <div className="lg:hidden w-full flex justify-between items-center mb-2 px-4 sm:px-0">
              <Sheet>
                <SheetTrigger asChild>
                  <button className="p-3 bg-white/70 dark:bg-zinc-900/70 rounded-2xl shadow-sm border border-zinc-200 dark:border-zinc-800 transition-transform active:scale-95 backdrop-blur-md hover:bg-white dark:hover:bg-zinc-900">
                    <Menu className="h-6 w-6 text-foreground" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85%] sm:w-[380px] p-0 border-r-0 bg-transparent flex flex-col justify-between overflow-hidden">
                  <SheetTitle className="sr-only">Información de Sistema</SheetTitle>
                  <SheetDescription className="sr-only">Detalles del punto de venta</SheetDescription>
                  {/* Backgrounds */}
                  <div className="absolute inset-0 z-0 bg-zinc-950">
                    {themes.map((t, i) => (
                      <div
                        key={t.name}
                        className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${i === currentThemeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${t.bgGradient}`} />
                        <div className={`absolute -top-[10%] -right-[10%] w-[30rem] h-[30rem] rounded-full ${t.blob1}/40 blur-[80px] mix-blend-color-dodge opacity-80 animate-[pulse_8s_ease-in-out_infinite]`} />
                        <div className={`absolute bottom-[-10%] -left-[10%] w-[20rem] h-[20rem] rounded-full ${t.blob2}/40 blur-[80px] mix-blend-color-dodge opacity-80 animate-[pulse_12s_ease-in-out_infinite_reverse]`} />
                      </div>
                    ))}
                  </div>
                  {/* Overlay Noise */}
                  <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay z-20 pointer-events-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }} />
                  {/* Content */}
                  <div className="relative z-30 flex flex-col justify-between h-full p-8 pb-12">
                    <div className="pt-4">
                      <Image src="/Logo247Stop.png" alt="24/7 STOP" width={180} height={180} className="object-contain drop-shadow-[0_15px_25px_rgba(0,0,0,0.5)]" priority />
                    </div>
                    <div className="space-y-6">
                      <h1 className="text-4xl font-extrabold text-white tracking-tight leading-[1.1] drop-shadow-xl">
                        {loginType === 'pos' ? 'Punto de Venta' : 'Administración'} <br />
                        <span className={`text-transparent bg-clip-text bg-gradient-to-r ${loginType === 'pos' ? theme.textPos : theme.textAdmin} drop-shadow-sm transition-all duration-1000`}>
                          Cloud-Based
                        </span>
                      </h1>
                      <p className="text-white/95 text-base font-medium leading-relaxed border-l-4 border-white/50 pl-4 drop-shadow-md">
                        Gestión total 24/7. Potenciado por <span className="text-white font-bold bg-black/20 px-2 py-0.5 rounded-md">NGI TECH</span> para llevar el control al siguiente nivel. Experiencia premium y vibrante.
                      </p>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              <button
                onClick={() => setCurrentThemeIndex((prev) => (prev + 1) % themes.length)}
                className="bg-white dark:bg-zinc-900 p-2.5 rounded-2xl shadow-xl border border-zinc-100 dark:border-zinc-800 transition-transform active:scale-95 duration-300"
                title="Cambiar color de tema"
              >
                <Image
                  src="/Logo247Stop.png"
                  alt="24/7 STOP"
                  width={80}
                  height={80}
                  className="object-contain"
                  priority
                />
              </button>
            </div>

            <div className="text-center lg:text-left space-y-2 px-4 sm:px-0 w-full">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                {loginType === 'pos' ? 'Bienvenido' : 'Administración'}
              </h2>
              <p className="text-base font-medium text-muted-foreground">
                {loginType === 'pos'
                  ? 'Ingresa con tu cédula y contraseña para acceder a la terminal.'
                  : 'Ingresa para acceder al panel de control y gestión.'}
              </p>
            </div>
          </div>

          <div className={`p-8 sm:p-10 bg-card/60 rounded-3xl border shadow-2xl shadow-zinc-200/50 dark:shadow-black/50 backdrop-blur-3xl relative transition-all duration-[1500ms] ${theme.formBorderHov} group`}>
            {/* Elegant colored top line based on theme */}
            <div className={`absolute -top-[1px] inset-x-8 h-[2px] bg-gradient-to-r from-transparent ${theme.formTopLine} to-transparent opacity-50 group-hover:opacity-100 transition-all duration-1000`} />

            <LoginForm loginType={loginType} onLoginTypeChange={setLoginType} />
          </div>

          <div className="flex items-center justify-center lg:justify-start pt-4">
            <div className="px-5 py-2 rounded-full bg-muted/50 border shadow-sm">
              <p className="text-xs font-bold text-muted-foreground tracking-widest uppercase flex items-center gap-2">
                ⚡ POWERED BY <span className="text-foreground">NGI TECH</span>
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
